import React, { useEffect, useMemo, useState } from "react";

import { FormControl, MenuItem, Select } from "@material-ui/core";
import { ActionMenu } from "@remar/shared/dist/components/ActionMenu";
import Button from "@remar/shared/dist/components/Button";
import { IColumn } from "@remar/shared/dist/components/MaterialTable";
import { ColumnHeader, StyledCellText } from "@remar/shared/dist/components/Table/styles";
import { EmptyState } from "@remar/shared/dist/layouts";
import WithTableContentLayout from "@remar/shared/dist/layouts/TableContentLayout";

import { NotificationAudienceTypeIdEnum, NotificationTypeId } from "@remar/shared/dist/models";
import { format } from "date-fns";
import {
	clearConflict,
	deleteNotification,
	fetchNotifications,
	selectBannersFullState
} from "store/features/Banners/banners.slice";

import { useAppDispatch, useAppSelector } from "store/hooks";

import { CreateNotificationDto, NotificationDto } from "store/services/notifications/dto";

import { IconListSvg } from "assets/icons";

import ConflictModal from "./ConflictModal/ConflictModal";
import CreateEditNotification from "./CreateNotification";
import { StatusMarker } from "./components";

const convertType = (type: NotificationTypeId) => {
	switch (type) {
		case NotificationTypeId.AdminGeneratedBanner:
			return "Banner";
		case NotificationTypeId.AdminGeneratedPush:
			return "Push";
		case NotificationTypeId.AdminGeneratedAnnouncement:
			return "Announcement";
		default:
			return type;
	}
};

const convertAudience = (audience: NotificationAudienceTypeIdEnum) => {
	switch (audience) {
		case NotificationAudienceTypeIdEnum.AllUsers:
			return "All users";
		case NotificationAudienceTypeIdEnum.PaidUsers:
			return "Paid users";
		case NotificationAudienceTypeIdEnum.TrialUsers:
			return "Trial users";
		default:
			return audience;
	}
};

const ManageNotifications = () => {
	const dispatch = useAppDispatch();

	useEffect(() => {
		dispatch(fetchNotifications({ page: 1, perPage: 10 }));
	}, [dispatch]);

	const tableColumns: Array<IColumn<NotificationDto>> = useMemo(
		() => [
			{
				align: "left",
				label: <ColumnHeader>Message</ColumnHeader>,
				width: 200,
				Cell: ({ rowData: { body } }) => <StyledCellText>{body}</StyledCellText>,
				dataKey: "message"
			},
			{
				align: "left",
				label: <ColumnHeader>Status</ColumnHeader>,
				width: 200,
				Cell: ({ rowData: { status } }) => (
					<StyledCellText>
						<StatusMarker status={status} /> {status}
					</StyledCellText>
				),
				dataKey: "status"
			},
			{
				align: "left",
				label: <ColumnHeader>Send Date & Time</ColumnHeader>,
				width: 200,
				Cell: ({ rowData: { sendAt, expiresAt } }) => (
					<StyledCellText>
						{format(new Date(sendAt), "LLL dd, yyyy - HH:mm")}
						{expiresAt && <br />}
						{expiresAt && format(new Date(expiresAt), "LLL dd, yyyy - HH:mm")}
					</StyledCellText>
				),
				dataKey: "date_time"
			},
			{
				align: "left",
				label: <ColumnHeader>Type</ColumnHeader>,
				width: 200,
				Cell: ({ rowData: { notificationTypeId } }) => (
					<StyledCellText>{convertType(notificationTypeId)}</StyledCellText>
				),
				dataKey: "type"
			},
			{
				align: "center",
				label: <ColumnHeader>Audience</ColumnHeader>,
				width: 200,
				Cell: ({ rowData: { notificationAudienceTypeId } }) => (
					<StyledCellText>{convertAudience(notificationAudienceTypeId)}</StyledCellText>
				),
				dataKey: "audience"
			},
			{
				align: "right",
				label: "",
				width: 100,
				Cell: ({ rowData }) => (
					<ActionMenu
						customMenuItems={[
							{
								label: "Edit",
								onClick: () => {
									setNotification(rowData);
								},
								visible: true,
								disabled: false
							},
							{
								label: "Delete",
								onClick: () => {
									dispatch(deleteNotification(rowData.id));
								},
								visible: true,
								disabled: false
							}
						]}
					/>
				)
			}
		],
		[dispatch]
	);

	const {
		conflict,
		notificationPerPage: perPage,
		notificationTotalItems: totalItems,
		notificationItems: items,
		isLoading
	} = useAppSelector(selectBannersFullState);

	const [notification, setNotification] = useState<CreateNotificationDto | null>(null);
	const [searchText, setSearchText] = useState("");
	const [page, setPage] = useState(1);
	const [deliveryMethod, setDeliveryMethod] = useState<NotificationTypeId | 0>(0);

	const handleSelectFilter = notificationTypeId => {
		setDeliveryMethod(notificationTypeId);
		dispatch(
			fetchNotifications({ page: 1, perPage, filters: { notificationTypeId: notificationTypeId || undefined } })
		);
	};

	const handleSearchBarChange = searchKeyword => {
		dispatch(fetchNotifications({ page: 1, perPage, searchKeyword }));
	};

	return (
		<WithTableContentLayout
			heading="Notifications"
			isLoading={isLoading}
			actions={
				<Button variant="filled" color="primary" onClick={() => setNotification({} as CreateNotificationDto)}>
					Create new Notification
				</Button>
			}
			tableTitle="Notifications"
			searchPlaceholder="Type to filter by keywords"
			searchText={searchText}
			handleSearchBarChange={handleSearchBarChange}
			setSearchText={setSearchText}
			filterBlock={
				<>
					<IconListSvg width={25} height={25} style={{ marginRight: "5px" }} />
					<FormControl hiddenLabel size="small" variant="filled">
						<Select
							disableUnderline
							value={deliveryMethod}
							onChange={event => handleSelectFilter(event.target.value)}
							displayEmpty
							MenuProps={{
								anchorOrigin: {
									vertical: "bottom",
									horizontal: "left"
								},
								transformOrigin: {
									vertical: "top",
									horizontal: "left"
								},
								getContentAnchorEl: null
							}}
						>
							<MenuItem value={0}>All Notifications</MenuItem>
							<MenuItem value={NotificationTypeId.AdminGeneratedBanner}>Banners</MenuItem>
							<MenuItem value={NotificationTypeId.AdminGeneratedPush}>Pushes</MenuItem>
							<MenuItem value={NotificationTypeId.AdminGeneratedAnnouncement}>Announcements</MenuItem>
						</Select>
					</FormControl>
				</>
			}
			emptyState={<EmptyState description="No notifications yet" />}
			onChangePage={() => setPage(p => p + 1)}
			totalItems={totalItems}
			totalEntities={totalItems}
			perPage={perPage}
			page={page}
			tableColumns={tableColumns}
			data={items}
		>
			<CreateEditNotification notification={notification} onClose={() => setNotification(null)} />
			<ConflictModal type={conflict} onClose={() => dispatch(clearConflict())} />
		</WithTableContentLayout>
	);
};

export default ManageNotifications;
